import React from 'react'
import * as Component from './Input.styles'
import PropTypes from 'prop-types'
import useOutsideClickHandler from 'hooks/outsideClickHandler'
import { useMobileSwitch } from 'hooks'

const Input = (props) => {
  const {
    className,
    id,
    placeholder,
    label,
    onChange,
    onFocus,
    onBlur,
    onEnter,
    required,
    startAdornment,
    endAdornment,
    loading,
    value,
    hasFocus,
    onDeleteButtonClicked,
    deleteButtonId,
    deleteButtonAriaLabel,
    hasError,
    errorMessage,
    dataCy,
    hasAutoComplete,
  } = props

  const containerRef = React.useRef()
  const focusInput = React.useRef(null)
  const [focus, setFocus] = React.useState(false)
  const isMobile = useMobileSwitch()

  useOutsideClickHandler(
    containerRef,
    () => {
      onBlur && onBlur()
      setFocus(false)
    },
    focus
  )

  const handleFocus = (event) => {
    onFocus && onFocus(event.target.value, id)
    setFocus(true)
  }

  const handleChange = (event) => {
    onChange(event.target.value, id)
  }

  const handleInputDelete = () => {
    focusInput.current.focus()
    onDeleteButtonClicked()
  }

  React.useEffect(() => {
    hasFocus && focusInput.current.focus()
  }, [hasFocus])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onEnter()
    }
  }

  /**
   * 1. mobile (message has more space)
   * 2. if longer error message (two lines)
   * 3. short error message (one line)
   * @returns {string}
   */
  const getTopStyle = () => {
    if (isMobile) return '-118%'
    if ((id === 'search' || id === 'location') && hasAutoComplete)
      return '-185%'
    return '-135%'
  }

  return (
    <>
      {label && <Component.Label htmlFor={ id }>{label}</Component.Label>}
      <Component.InputContainer
        ref={ containerRef }
        hasError={ hasError }
        focus={ focus }
        className={ className }>
        {startAdornment && (
          <Component.StartAdornment>{startAdornment}</Component.StartAdornment>
        )}

        <Component.Input
          ref={ focusInput }
          id={ id }
          placeholder={ placeholder }
          value={ value }
          required={ required }
          autoComplete='off'
          onFocus={ handleFocus }
          onChange={ handleChange }
          onKeyDown={ handleKeyDown }
          data-cy={ dataCy }
        />

        {hasError && (
          <Component.ErrorMessage
            topStyle={ getTopStyle() }
            data-cy={ `input-${ id }-error` }>
            {errorMessage}
          </Component.ErrorMessage>
        )}

        {value !== '' && !loading && (
          <Component.DeleteButton
            id={ deleteButtonId }
            aria-label={ deleteButtonAriaLabel }
            className='icon-f-cross'
            onClick={ () => handleInputDelete() }
          />
        )}

        {loading && (
          <Component.LoadingIcon
            id='locationLoadingIcon'
            className='icon-f-load'
            isSpinning={ true }
          />
        )}

        {endAdornment && (
          <Component.EndAdornment>{endAdornment}</Component.EndAdornment>
        )}
      </Component.InputContainer>
    </>
  )
}

Input.defaultProps = {
  required: false,
  loading: false,
  value: '',
  hasFocus: false,
  hasError: false,
  hasAutoComplete: false,
}

Input.propTypes = {
  /** The CSS class name of the input wrapper element. */
  className: PropTypes.string,
  /** The id of the input element */
  id: PropTypes.string.isRequired,
  /** The short hint displayed in the input before the user enters a value */
  placeholder: PropTypes.string,
  /** The label content */
  label: PropTypes.string,
  /** The value o the input element */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** If true the input gets focus */
  hasFocus: PropTypes.bool,
  /** Callback fired when the value is changed
   * Signature: function(text: string, id: string) {...} */
  onChange: PropTypes.func.isRequired,
  /** Callback fired when input gets focus */
  onFocus: PropTypes.func,
  /** Callback fired when input loses focus */
  onBlur: PropTypes.func,
  /** Callback fired when enter key was pressed */
  onEnter: PropTypes.func,
  /** If true the input element will be required */
  required: PropTypes.bool,
  /** Start adornment for this component */
  startAdornment: PropTypes.node,
  /** End adornment for this component */
  endAdornment: PropTypes.node,
  /** If true, display loading icon before end adornment */
  loading: PropTypes.bool,
  /** Callback fired when input should reset */
  onDeleteButtonClicked: PropTypes.func.isRequired,
  /** Id of the delete button */
  deleteButtonId: PropTypes.string,
  /** aria label for delete button */
  deleteButtonAriaLabel: PropTypes.string,
  /* Triggers display of error message **/
  hasError: PropTypes.bool,
  /** Error message to be shown if errors occur - only required if errors exist */
  errorMessage(props, propName, componentName) {
    if (
      props.hasError &&
      (props[propName] === undefined || typeof props[propName] !== 'string')
    ) {
      return new Error(
        `Invalid prop ${ propName } supplied to ${ componentName }. ${ propName } must be of type string and contain a string`
      )
    }
  },
  dataCy: PropTypes.string,
  hasAutoComplete: PropTypes.bool,
}

export default Input
